import { m } from "framer-motion";
import { Link } from "react-router-dom";
import { Utensils, ShoppingBag, Dumbbell, Store, Building2, Warehouse, ArrowUpRight } from "lucide-react";
import { BUSINESS } from "@/data/site";

const ease = [0.22, 1, 0.36, 1];

const SECTORS = [
  { icon: Utensils, label: "Restaurants" },
  { icon: ShoppingBag, label: "Takeaways" },
  { icon: Dumbbell, label: "Padel centres & gyms" },
  { icon: Store, label: "Retail units" },
  { icon: Building2, label: "Offices & workspaces" },
  { icon: Warehouse, label: "Warehouses & units" },
];

export const CommercialFitout = () => (
  <section id="commercial" data-testid="commercial-fitout" className="py-24 lg:py-36 border-t border-hairline">
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Image */}
        <m.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease }}
          className="lg:col-span-6 relative"
        >
          <div className="relative overflow-hidden">
            <img
              src="/gallery/commercial.webp"
              alt="The Aims Electrical team on a large commercial fit-out project"
              loading="lazy"
              decoding="async"
              className="w-full aspect-[16/10] object-cover"
            />
          </div>
          <div className="absolute -bottom-5 left-5 bg-brand text-void px-6 py-4 shadow-lg" data-testid="commercial-badge">
            <div className="font-heading text-3xl leading-none tracking-tight">Full teams</div>
            <div className="font-mono text-[11px] tracking-[0.15em] mt-1">ON-SITE, START TO FINISH</div>
          </div>
        </m.div>

        {/* Copy */}
        <div className="lg:col-span-6">
          <m.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease }}
            className="font-mono text-xs tracking-[0.25em] text-brand mb-6 flex items-center gap-3"
          >
            <span className="inline-block w-8 h-px bg-brand" />
            COMMERCIAL FIT-OUTS
          </m.p>

          <h2 className="font-heading uppercase leading-[0.9] tracking-tight text-4xl sm:text-5xl lg:text-6xl text-stark">
            Full commercial<br />fit-outs.
          </h2>

          <p className="mt-8 text-concrete leading-relaxed max-w-xl">
            {BUSINESS.name} delivers complete electrical fit-outs for commercial spaces across {BUSINESS.region}.
            From first fix to final testing and sign-off, our team handles the whole job — power, lighting,
            containment, data and safety systems — working to programme and to the highest standard.
          </p>
          <p className="mt-4 text-concrete leading-relaxed max-w-xl">
            We regularly fit out restaurants, takeaways, padel centres, gyms, retail units and offices,
            bringing a full crew on-site to keep your project moving and open on time.
          </p>

          <ul className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-4">
            {SECTORS.map(({ icon: Icon, label }) => (
              <li key={label} className="glossy-card group flex flex-col items-start gap-4 p-5">
                <span className="glossy-icon grid place-items-center w-12 h-12 rounded-xl shrink-0">
                  <Icon className="w-6 h-6 text-white" strokeWidth={2} />
                </span>
                <span className="font-heading uppercase tracking-tight text-sm sm:text-base text-white leading-tight">{label}</span>
              </li>
            ))}
          </ul>

          <Link
            to="/contact"
            data-testid="commercial-cta"
            className="group mt-10 inline-flex items-center gap-3 bg-brand text-void font-medium px-7 py-4 hover:bg-stark transition-colors duration-200"
          >
            Discuss your project
            <ArrowUpRight className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>
    </div>
  </section>
);
